import { localUrl as appLocalUrl } from '../app-path.js';
import { swapElementTag } from './dom-utils';
import locationsModal from './locations-modal';
import {
  fetchWeatherJSON, geolocationPermission, weatherNumber, weatherText,
  weatherIcon, weatherQualityState, weatherCityUrls, setWeatherImage, reportWeatherUnavailable
} from './home-weather-client';

let activeLoad;

const updateColumn = ($column, forecast = {}) => {
  const min = weatherNumber(forecast?.temperature?.min);
  const max = weatherNumber(forecast?.temperature?.max);
  const icon = weatherIcon(forecast?.textIcon?.icon?.day);
  const description = weatherText(forecast?.textIcon?.text?.reducedPhrasePt);
  setWeatherImage($column.find('.card-forecast-three-days-column-icon'), icon, weatherText(forecast?.textIcon?.text?.pt));
  [['min', min], ['max', max]].forEach(([key, value]) => {
    const $field = $column.find(`.card-forecast-three-days-column-temp-${key}`);
    $field.text(value === null ? 'Sem dados' : `${value}°`);
    if (value === null) $field.attr('data-weather-unavailable', `home-temperature-${key}`);
    else $field.removeAttr('data-weather-unavailable');
  });
  const $description = $column.find('.card-forecast-three-days-column-description');
  $description.text(description || 'Sem dados');
  if (description) $description.removeAttr('data-weather-unavailable');
  else $description.attr('data-weather-unavailable', 'home-description');
  return { usable: min !== null || max !== null || !!icon || !!description, complete: min !== null && max !== null && !!icon && !!description };
};

export const CardForecastThreeDays = {
  markUnavailable: function (context) {
    if (context && !context.isCurrent()) return;
    const $card = $('.card-forecast-three-days-wrapper');
    if ($card.length) reportWeatherUnavailable('three_days', context);
    $card.attr({ 'data-weather-state': 'unavailable', 'aria-busy': 'false' });
    $card.find('.shimmer-placeholder').removeClass('shimmer-placeholder');
    $card.find('.card-forecast-three-days-column').each(function () {
      updateColumn($(this));
    });
  },

  load: async function (idlocale, todayUrl, city15DaysUrl, options = {}) {
    if (options.context && !options.context.isCurrent()) return false;
    // Standalone callers also supersede an earlier card load.
    activeLoad?.abort();
    const controller = new AbortController();
    activeLoad = controller;
    const parent = options.context;
    const cancel = () => controller.abort();
    if (parent?.signal.aborted) cancel();
    else parent?.signal.addEventListener('abort', cancel, { once: true });
    const context = {
      signal: controller.signal,
      isCurrent: () => activeLoad === controller && (!parent || parent.isCurrent())
    };
    if (!context.isCurrent()) return false;
    const $card = $('.card-forecast-three-days-wrapper');
    $card.attr({ 'data-weather-state': 'loading', 'aria-busy': 'true' });
    $card.find('.shimmer-placeholder').removeClass('shimmer-placeholder');

    try {
      const forecastPromise = options.forecastPromise || fetchWeatherJSON(
        appLocalUrl(`/json/myclimatempo/user/forecastFiveDays?idlocale=${idlocale}`),
        { signal: context.signal }
      );
      const forecastResult = await forecastPromise;
      if (!context.isCurrent() || context.signal.aborted) return false;
      const forecasts = Array.isArray(forecastResult?.data?.getFiveDaysForecast)
        ? forecastResult.data.getFiveDaysForecast : [];
      if (weatherQualityState(forecastResult, forecasts.length > 0) === 'unavailable') {
        CardForecastThreeDays.markUnavailable(context);
        return false;
      }
      const status = await geolocationPermission(context.signal);
      if (!context.isCurrent() || context.signal.aborted) return false;

      const urls = weatherCityUrls(forecastResult?.infoCity);
      todayUrl = urls?.todayUrl || todayUrl;
      city15DaysUrl = urls?.city15DaysUrl || city15DaysUrl;
      const $link = $('#card-forecast-three-days-location');
      if ($link.length) {
        const enabled = status === 'granted' && !!todayUrl;
        const node = swapElementTag($link[0], enabled ? 'a' : 'button');
        const $cta = $(node);
        $cta.attr({
          role: enabled ? 'link' : 'button',
          'data-id': enabled ? 'Card_WeatherForecast_Link_Location' : 'Card_WeatherForecast_Link_UseLocation',
          'data-location-state': enabled ? 'enabled' : 'disabled'
        }).off('click.ct3d');
        if (enabled) $cta.attr('href', todayUrl);
        else {
          $cta.removeAttr('href').attr('type', 'button').on('click.ct3d', function (event) {
            if (!context.isCurrent()) return;
            event.preventDefault();
            locationsModal.open();
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ event: 'click_button', id: 'Card_WeatherForecast_Link_UseLocation' });
          });
        }
      }
      const city = weatherText(forecastResult?.infoCity?.city);
      if (city) $('.card-forecast-three-days-location').text(city);

      let usable = false;
      let complete = true;
      const dated = forecasts.some(forecast => weatherText(forecast?.date));
      $card.find('.card-forecast-three-days-column').each(function (index) {
        const $column = $(this);
        const position = Number($column.attr('data-forecast-index') ?? index);
        const date = $column.attr('data-date')?.slice(0, 10);
        // Dated rows match the permanent day, never a filtered icon list.
        const forecast = dated && date
          ? forecasts.find(row => row?.date?.slice(0, 10) === date)
          : forecasts[position];
        const quality = updateColumn($column, forecast);
        usable = usable || quality.usable;
        complete = complete && quality.complete;
        if (position === 2) {
          $column.find('.card-forecast-three-days-column-day').text(weatherText(forecast?.dayWeekFullMin) || '-');
        }
      });
      if (city15DaysUrl) $('.button-see-full-forecast-card-forecast-three-days').attr('href', city15DaysUrl);
      const state = weatherQualityState(forecastResult, usable, complete);
      if (state === 'unavailable' && $card.length) reportWeatherUnavailable('three_days', context);
      $card.attr({ 'data-weather-state': state, 'aria-busy': 'false' });
      return state === 'success';
    } catch (error) {
      if (!context.isCurrent()) return false;
      CardForecastThreeDays.markUnavailable(context);
      return false;
    } finally {
      parent?.signal.removeEventListener('abort', cancel);
    }
  }
};
